import Vue from 'vue'
import Router from 'vue-router'
import store from './store/store'
import Submission from './models/Submission.js'
import Change from './models/Change.js'
import Entity from './models/Entity.js'
import Officer from './models/Officer.js'
import SubmissionService from './services/SubmissionService'

Vue.use(Router)

const CreateLocalCopy = obj => JSON.parse(JSON.stringify(obj))

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      name: 'home',
      meta: {
        order: 1,
        hasBackButton: false,
        hasNextButton: false,
        requiresData: false,
        requiresAuthentication: false,
        transition: 'fade-in'
      },
      component: () => import('./views/Home.vue')
    },
    {
      path: '/register',
      name: 'account/register',
      meta: {
        order: 2,
        hasBackButton: false,
        hasNextButton: false,
        requiresData: false,
        requiresAuthentication: false
      },
      component: () => import('./views/Register.vue')
    },
    {
      path: '/login',
      name: 'account/login',
      meta: {
        order: 2,
        hasBackButton: false,
        hasNextButton: false,
        requiresData: false,
        requiresAuthentication: false
      },
      component: () => import('./views/Login.vue')
    },
    {
      path: '/forgot',
      name: 'account/forgot',
      meta: {
        order: 3,
        hasBackButton: false,
        hasNextButton: false,
        requiresData: false,
        requiresAuthentication: false
      },
      component: () => import('./views/ForgotPassword.vue')
    },
    {
      path: '/reset/:token',
      name: 'account/reset',
      props: true,
      meta: {
        order: 2,
        hasBackButton: false,
        hasNextButton: false,
        requiresData: false,
        requiresAuthentication: false
      },
      component: () => import('./views/ResetPassword.vue')
    },
    {
      path: '/account/update',
      name: 'account/update',
      props: true,
      meta: {
        order: 2,
        hasBackButton: false,
        hasNextButton: false,
        requiresData: false
      },
      component: () => import('./views/AccountUpdate.vue'),
      beforeEnter(to, from, next) {
        store.dispatch('user/getInfo').then(res => {
          to.params.userProp = res.data
          next()
        })
      }
    },
    {
      path: '/submission/search',
      name: 'submission/list',
      meta: {
        order: 2,
        hasBackButton: false,
        hasNextButton: false
      },
      component: () => import('./views/SubmissionSearch.vue'),
      beforeEnter(to, from, next) {
        if (store.state.user.user.role == 'dco') return next({ name: 'submission/dco/search' })
        store.dispatch('submission/getSubmissions').then(() => {
          next()
        }).catch(() => {
          next()
        })
      }
    },
    {
      path: '/faq/faq',
      name: 'faq/faq',
      meta: {
        order: 2,
        hasBackButton: false,
        hasNextButton: false,
        requiresData: false,
        requiresAuthentication: false
      },
      component: () => import('./views/FAQ.vue')
    },
    {
      path: '/submission/dco/search',
      name: 'submission/dco/search',
      meta: {
        order: 2,
        hasBackButton: false,
        hasNextButton: false
      },
      component: () => import('./views/SubmissionDCOSearch.vue'),
      beforeEnter(to, from, next) {
        if (store.state.user.user.role != 'dco') return next({ name: 'submission/list' })
        else next()
      }
    },
    {
      path: '/submission/create/new',
      name: 'submission/create',
      beforeEnter(to, from, next) {
        Promise.all([store.dispatch('submission/save', new Submission()), store.dispatch('change/clear'), store.dispatch('entity/clear'), store.dispatch('document/clear'), store.dispatch('officer/clear')]).then(() => {
          next({ name: 'change/list', params: { submissionId: store.state.submission.submission.submissionID }})
        })
      }
    },
    {
      path: '/submission/:submissionId',
      name: 'change/list',
      meta: {
        order: 3,
        nextButtonText: 'Review ERM‑14 Form',
        requiresSubmission: true,
        breadcrumbs: () => {
          return [ 
            { text: 'Home', to: { name: 'home' }},
            { text: 'ERM‑14 Form', active: true },
          ]
        }
      },
      component: () => import('./views/Changes.vue')
    },
    {
      path: '/submission/:submissionId/change/:changeId?',
      name: 'change/edit',
      meta: {
        order: 4,
        requiresSubmission: true,
        breadcrumbs: routeParams => {
          return [
            { text: 'Home', to: { name: 'home' }},
            { text: 'ERM‑14 Form', to: { name: 'change/list', params: { submissionId: routeParams.submissionId } }},
            { text: 'Ownership Change', active: true }
          ]
        }
      },
      component: () => import('./views/Change.vue'),
      props: true,
      beforeEnter(to, from, next) {
        const id = to.params.changeId
        if (id) {
          store.dispatch('change/set', id).then(change => {
            to.params.changeProp = CreateLocalCopy(change)
            next()            
          })
        } else {
          to.params.changeProp = new Change(to.params.submissionId)
          next()
        }
      }
    },
    {
      path: '/submission/:submissionId/change/:changeId/entities',
      name: 'entity/list',
      meta: {
        order: 5,
        nextButtonText: 'Additional Questions',
        requiresSubmission: true,
        breadcrumbs: routeParams => {
          return [
            { text: 'Home', to: { name: 'home' }},
            { text: 'ERM‑14 Form', to: { name: 'change/list', params: { submissionId: routeParams.submissionId } }},
            { text: 'Ownership Change', to: { name: 'change/edit', params: { submissionId: routeParams.submissionId, changeId: routeParams.changeId }}},
            { text: 'Entities', active: true }
          ]
        }
      },
      component: () => import('./views/Entities.vue')
    },
    {
      path: '/submission/:submissionId/change/:changeId/entity/:entityId?',
      name: 'entity/edit',
      meta: {
        order: 6,
        requiresSubmission: true,
        breadcrumbs: routeParams => {
          return [
            { text: 'Home', to: { name: 'home' }},
            { text: 'ERM‑14 Form', to: { name: 'change/list', params: { submissionId: routeParams.submissionId } }},
            { text: 'Ownership Change', to: { name: 'change/edit', params: { submissionId: routeParams.submissionId, changeId: routeParams.changeId }}},
            { text: 'Entities', to: { name: 'entity/list', params: { submissionId: routeParams.submissionId, changeId: routeParams.changeId }}},
            { text: 'Entity', active: true }
          ]
        }
      },
      component: () => import('./views/Entity.vue'),
      props: true,
      beforeEnter(to, from, next) {
        const id = to.params.entityId
        if (id) {
          store.dispatch('entity/set', id).then(entity => {
            to.params.entityProp = CreateLocalCopy(entity)
            next()
          })
        } else {
          to.params.entityProp = new Entity(
            to.params.submissionId,
            to.params.changeId,
            to.params.before
          )
          next()
        }
      }
    },
    {
      path:
        '/submission/:submissionId/change/:changeId/entity/:entityId/officers',
      name: 'officer/list',
      meta: {
        order: 7,
        nextButtonText: 'Done Adding Officers',
        requiresSubmission: true,
        breadcrumbs: routeParams => {
          return [
            { text: 'Home', to: { name: 'home' }},
            { text: 'ERM‑14 Form', to: { name: 'change/list', params: { submissionId: routeParams.submissionId } }},
            { text: 'Ownership Change', to: { name: 'change/edit', params: { submissionId: routeParams.submissionId, changeId: routeParams.changeId }}},
            { text: 'Entities', to: { name: 'entity/list', params: { submissionId: routeParams.submissionId, changeId: routeParams.changeId }}},
            { text: 'Entity', to: { name: 'entity/edit', params: { submissionId: routeParams.submissionId, changeId: routeParams.changeId, entityId: routeParams.entityId }}},
            { text: 'Officers', active: true }
          ]
        }
      },
      component: () => import('./views/Officers.vue')
    },
    {
      path:
        '/submission/:submissionId/change/:changeId/entity/:entityId/officer/:officerId?',
      name: 'officer/edit',
      meta: {
        order: 8,
        nextButtonText: 'Done With Officer',
        requiresSubmission: true,
        breadcrumbs: routeParams => {
          return [
            { text: 'Home', to: { name: 'home' }},
            { text: 'ERM‑14 Form', to: { name: 'change/list', params: { submissionId: routeParams.submissionId } }},
            { text: 'Ownership Change', to: { name: 'change/edit', params: { submissionId: routeParams.submissionId, changeId: routeParams.changeId }}},
            { text: 'Entities', to: { name: 'entity/list', params: { submissionId: routeParams.submissionId, changeId: routeParams.changeId }}},
            { text: 'Entity', to: { name: 'entity/edit', params: { submissionId: routeParams.submissionId, changeId: routeParams.changeId, entityId: routeParams.entityId }}},
            { text: 'Officers', to: { name: 'officer/list', params: { submissionId: routeParams.submissionId, changeId: routeParams.changeId, entityId: routeParams.entityId }}},
            { text: 'Officer', active: true }
          ]
        }
      },
      component: () => import('./views/Officer.vue'),
      props: true,
      beforeEnter(to, from, next) {
        const id = to.params.officerId
        if (id) {
          store.dispatch('officer/set', id).then(officer => {
            to.params.officerProp = CreateLocalCopy(officer)
            next()
          })
        } else {
          to.params.officerProp = new Officer(
            to.params.submissionId,
            to.params.changeId,
            to.params.entityId
          )
          next()
        }
      }
    },
    {
      path: '/submission/:submissionId/change/:changeId/questions',
      name: 'questions',
      meta: {
        order: 9,
        nextButtonText: 'Continue',
        requiresSubmission: true,
        breadcrumbs: routeParams => {
          return [
            { text: 'Home', to: { name: 'home' }},
            { text: 'ERM‑14 Form', to: { name: 'change/list', params: { submissionId: routeParams.submissionId } }},
            { text: 'Ownership Change', to: { name: 'change/edit', params: { submissionId: routeParams.submissionId, changeId: routeParams.changeId }}},
            { text: 'Additional Questions', active: true }
          ]
        }
      },
      component: () => import('./views/Questions.vue'),
      props: true,
      beforeEnter(to, from, next) {
        store.dispatch('change/set', to.params.changeId).then(change => {
          to.params.changeProp = CreateLocalCopy(change)
          next()
        })
      }
    },
    {
      path: '/submission/:submissionId/change/:changeId/documents',
      name: 'documents',
      meta: {
        order: 10,
        nextButtonText: 'Done With Change',
        requiresSubmission: true,
        breadcrumbs: routeParams => {
          return [
            { text: 'Home', to: { name: 'home' }},
            { text: 'ERM‑14 Form', to: { name: 'change/list', params: { submissionId: routeParams.submissionId } }},
            { text: 'Ownership Change', to: { name: 'change/edit', params: { submissionId: routeParams.submissionId, changeId: routeParams.changeId }}},
            { text: 'Documents', active: true }
          ]
        }
      },
      component: () => import('./views/Documents.vue'),
      beforeEnter(to, from, next) {
        store.dispatch('change/set', to.params.changeId).then(() => {
          next()
        })
      }
    },
    {
      path: '/submission/:submissionId/review',
      name: 'review',
      meta: {
        order: 11,
        nextButtonText: 'Certification',
        requiresSubmission: true,
        breadcrumbs: routeParams => {
          return [
            { text: 'Home', to: { name: 'home' }},
            { text: 'ERM‑14 Form', to: { name: 'change/list', params: { submissionId: routeParams.submissionId } }},
            { text: 'Review', active: true }
          ]
        }
      },
      component: () => import('./views/Review.vue')
    },
    {
      path: '/submission/:submissionId/certification',
      name: 'certification',
      meta: {
        order: 12,
        hasNextButton: false,
        backButtonText: 'Review',
        requiresSubmission: true,
        breadcrumbs: routeParams => {
          return [
            { text: 'Home', to: { name: 'home' }},
            { text: 'ERM‑14 Form', to: { name: 'change/list', params: { submissionId: routeParams.submissionId } }},
            { text: 'Review', to: { name: 'review', params: { submissionId: routeParams.submissionId }}},
            { text: 'Certification', active: true }
          ]
        }
      },
      component: () => import('./views/Certification.vue'),
      props: true,
      beforeEnter(to, from, next) {
        to.params.submissionProp = CreateLocalCopy(store.state.submission.submission)
        next()
      }
    }
  ]
})

//Makes sure the current user is set in Vuex. If not, attempts to load the user out of localStorage and stores it into Vuex.
router.beforeEach((to, from, next) => {
  let user = store.state.user.user
  if (!user.email) {
    user = JSON.parse(window.localStorage.getItem('user'))
    const token = window.localStorage.getItem('token')
    const refreshToken = window.localStorage.getItem('refreshToken')
    if (user) { 
      store.dispatch('user/setUserData', { email: user.email, name: user.name, role: user.role, token, refreshToken }).then(() => {
        next()
      })
    } else next()
  } else next()
})

//Makes sure the user is logged in for routes that require a logged-in user.
router.beforeEach((to, from, next) => {
  const meta = to.matched[0].meta
  if (meta.requiresAuthentication === false || store.state.user.user.email) return next()

  next({ name: 'account/login' })
})

//Makes sure the required data (change types, officer titles, list of states, etc.) is loaded, otherwise loads it if required for this route.
router.beforeEach((to, from, next) => {
  const meta = to.matched[0].meta
  if (meta.requiresData === false || store.state.application.changeTypes.length || store.state.application.entityTypes.length || store.state.application.officerTitles.length) return next()

  SubmissionService.getRequiredData().then(res => {
    Promise.all([
      store.dispatch('application/setChangeTypes', [{ changeTypeID: 0, shortDescription: 'Select a change type' }, ...res.data.changeTypes]),
      store.dispatch('application/setEntityTypes', res.data.entityTypes),
      store.dispatch('application/setOfficerTitles', res.data.officerTitles),
      store.dispatch('application/setStates', res.data.states)
    ]).finally(() => {
      next()
    })    
  }, () => {
  })
})

//Unset the current submission if the user goes to the search or home screens
router.beforeEach((to, from, next) => {
  const name = to.name
  if (name == 'home' || name == 'submission/list') {
    Promise.all([
      store.dispatch('submission/clearSubmission'),
      store.dispatch('change/clearChange'),
      store.dispatch('entity/clearEntity'),
      store.dispatch('officer/clearOfficer')
    ]).finally(() => {
      next()
    })
  } else {
    next()
  }
})

//Makes sure the full submission data has already been loaded if the route requires a submission.
router.beforeEach((to, from, next) => {
  const meta = to.matched[0].meta
  if (meta.requiresSubmission) {
    const id = to.params.submissionId
    if (!id || (store.state.submission.submission && store.state.submission.submission.submissionID == id)) return next()

    store.dispatch('submission/getFull', id).then(() => {
      Promise.all([
        store.dispatch('change/set', to.params.changeId),
        store.dispatch('entity/set', to.params.entityId),
        store.dispatch('officer/set', to.params.officerId),
      ]).finally(() => {
        next()
      })      
    }, () => {})
  }

  next()
})

//Sets breadcrumb, button text, and transition for the route.
router.beforeEach((to, from, next) => {
  const meta = to.matched[0].meta // assumes only one route is matched
  const transition = meta.transition || ((meta.order || 0) >= from.meta.order ? 'move-next' : 'move-back')

  const showBackButton = meta.hasBackButton !== false
  const showNextButton = meta.hasNextButton !== false
  const backButtonText = meta.backButtonText || 'Back'
  const nextButtonText = meta.nextButtonText || 'Next'

  const breadcrumbs = meta.breadcrumbs && typeof meta.breadcrumbs === 'function' ? meta.breadcrumbs(to.params) : []

  Promise.all([
    store.dispatch('application/setTransition', transition),
    store.dispatch('application/setButtonData', { showBackButton, showNextButton, backButtonText, nextButtonText }),
    store.dispatch('application/setBreadcrumbs', breadcrumbs),
  ]).finally(() => next())
})
export default router
